<template>
  <div class="preview-wrap">
    <div class="d-flex align-items-center preview-hint">
      <small class="text-muted">
        <template v-if="!editable">&nbsp;</template>
        <template v-else-if="activeRow">
          Row selected — use the buttons beside it to add or remove a line.
        </template>
        <template v-else>
          Click inside a table row (e.g. a medicine) to add or remove rows.
        </template>
      </small>
      <b-button
        v-if="editable && misplaced"
        size="sm"
        variant="outline-warning"
        class="ml-2 py-0"
        title="Move text that ended up in a cell's small second line back up to the first"
        @click="fixMisplaced"
      >
        Fix {{ misplaced }} misplaced line{{ misplaced === 1 ? "" : "s" }}
      </b-button>
    </div>
    <iframe
      ref="frame"
      class="html-preview border rounded bg-white"
      title="Prescription preview"
      @load="onLoad"
    ></iframe>
    <!-- The row controls are drawn over the iframe rather than inside it:
         anything injected into the prescription would be picked up by the save
         (documentElement.outerHTML) and end up in the patient's PDF. -->
    <template v-if="editable && rowBox">
      <div class="row-outline" :style="outlineStyle"></div>
      <div class="row-tools" :style="toolsStyle">
        <b-button
          size="sm"
          variant="primary"
          title="Add a row below this one"
          @click="addRowBelow"
        >
          + Row
        </b-button>
        <b-button
          size="sm"
          variant="danger"
          class="mt-1"
          title="Delete this row"
          :disabled="!canDeleteRow"
          @click="deleteRow"
        >
          Delete
        </b-button>
      </div>
    </template>
  </div>
</template>

<script>
// A cell emptied down to nothing — or holding only an empty <strong> — offers
// the caret no position to sit in, so the browser slides it to the nearest one
// it can find, which is the row above. This invisible character gives the caret
// a home inside the cell's own markup; it is stripped from the html we publish.
const CARET_ANCHOR = "\u200b";

// Renders the prescription inside an iframe rather than v-html, for two reasons:
// the prescription's own <style> block targets *, body, table, th and td, which
// would restyle the whole admin page if injected inline; and an isolated
// document gives us a body we can make contenteditable for WYSIWYG editing.
export default {
  name: "HtmlPreview",
  props: {
    value: {
      type: String,
      default: "",
    },
    editable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Set while a change originated inside the iframe, so the watcher below
      // does not reload the document and throw the caret back to the start.
      selfEdit: false,
      debounce: null,
      reloadTimer: null,
      pendingScroll: 0,
      pendingPrint: false,
      // <tr> holding the caret, or null when the caret is outside a data row.
      // Always replaced rather than mutated, so the buttons stay in sync.
      activeRow: null,
      // Where that row sits, in this component's coordinates. Null hides the
      // controls — including when the row scrolls out of the iframe's view.
      rowBox: null,
      // Cells left by the earlier caret bug, with their text in the second line
      // and nothing on the first.
      misplaced: 0,
      // The prescription's own style attribute on <body>, before editing added
      // the focus outline to it.
      bodyStyle: null,
    };
  },
  computed: {
    // Emptying a table by deleting its last row leaves markup the doctor cannot
    // click back into, so keep one row.
    canDeleteRow() {
      const row = this.activeRow;
      if (!row || !row.parentNode) return false;
      return this.rowsOf(row.parentNode).length > 1;
    },
    outlineStyle() {
      const box = this.rowBox;
      if (!box) return null;
      return {
        top: `${box.top}px`,
        left: `${box.left}px`,
        width: `${box.width}px`,
        height: `${box.height}px`,
      };
    },
    // Anchored to the right inside edge of the iframe and centred on the row,
    // so the controls read as a gutter and never cover the drug name.
    toolsStyle() {
      const box = this.rowBox;
      if (!box) return null;
      return {
        top: `${box.centerY}px`,
        left: `${box.gutterX}px`,
      };
    },
  },
  watch: {
    value(next) {
      if (this.selfEdit) {
        this.selfEdit = false;
        return;
      }
      // The value can arrive in pieces while the prescription loads; settle
      // first rather than reloading the document for each change.
      clearTimeout(this.reloadTimer);
      this.reloadTimer = setTimeout(() => this.render(next), 400);
    },
    editable(next) {
      const doc = this.doc();
      if (doc && doc.body) doc.body.contentEditable = next ? "true" : "false";
      if (!next) {
        this.activeRow = null;
        this.rowBox = null;
      }
      this.countMisplaced();
    },
  },
  mounted() {
    this.render(this.value);
    window.addEventListener("resize", this.measureRow);
  },
  beforeDestroy() {
    clearTimeout(this.debounce);
    clearTimeout(this.reloadTimer);
    window.removeEventListener("resize", this.measureRow);
  },
  methods: {
    doc() {
      const frame = this.$refs.frame;
      return frame ? frame.contentDocument : null;
    },
    render(html) {
      const frame = this.$refs.frame;
      if (!frame) return;
      // The old document is about to go away, so the row it held must not be
      // handed to the buttons.
      this.activeRow = null;
      this.rowBox = null;
      // Reloading resets the scroll box, so carry the position across.
      const win = frame.contentWindow;
      this.pendingScroll = win ? win.scrollY || 0 : 0;
      frame.srcdoc = html || "";
    },
    onLoad() {
      const doc = this.doc();
      if (!doc || !doc.body) return;
      // Remembered before we touch it, so the saved html can be handed back the
      // prescription's own body styling rather than ours.
      this.bodyStyle = doc.body.getAttribute("style");
      doc.body.contentEditable = this.editable ? "true" : "false";
      doc.body.style.outline = "none";
      doc.addEventListener("input", this.onInput);
      // selectionchange is the reliable signal here; the others cover browsers
      // that fire it late when the caret moves by click or arrow key.
      doc.addEventListener("selectionchange", this.syncActiveRow);
      doc.addEventListener("mouseup", this.syncActiveRow);
      doc.addEventListener("keyup", this.syncActiveRow);
      doc.addEventListener("keydown", this.onKeydown);
      // The row moves under the overlay when the document scrolls, and changes
      // height as typed text wraps, so the controls have to follow both.
      const win = this.$refs.frame.contentWindow;
      if (win) win.addEventListener("scroll", this.measureRow);
      doc.addEventListener("input", this.measureRow);
      if (this.pendingScroll) {
        this.$refs.frame.contentWindow.scrollTo(0, this.pendingScroll);
        this.pendingScroll = 0;
      }
      if (this.pendingPrint) {
        this.pendingPrint = false;
        this.$nextTick(() => this.sendToPrinter());
      }
      this.countMisplaced();
    },
    // Prints the iframe itself, so the browser's print dialog gets only the
    // prescription document and no new tab has to be opened for it.
    print() {
      if (!this.value) return false;
      // A queued reload would otherwise print stale content.
      if (this.reloadTimer) {
        clearTimeout(this.reloadTimer);
        this.reloadTimer = null;
        this.pendingPrint = true;
        this.render(this.value);
        return true;
      }
      this.sendToPrinter();
      return true;
    },
    sendToPrinter() {
      const frame = this.$refs.frame;
      if (!frame || !frame.contentWindow) return;
      frame.contentWindow.focus();
      frame.contentWindow.print();
    },
    onInput() {
      clearTimeout(this.debounce);
      // Coalesce keystrokes so the form is not updated on every letter.
      this.debounce = setTimeout(this.emitNow, 300);
    },
    // Row edits are single deliberate actions, so they publish immediately
    // instead of waiting out the typing debounce.
    emitNow() {
      clearTimeout(this.debounce);
      const doc = this.doc();
      if (!doc) return;
      this.selfEdit = true;
      this.$emit("input", `<!doctype html>\n${this.serialize(doc)}`);
      this.countMisplaced();
    },
    // Serializes a copy of the document. The caret anchors, the contenteditable
    // flag and the focus outline are all scaffolding for editing — none of it
    // belongs in the prescription that is saved, printed and sent to the
    // patient. Working on a clone also keeps the live caret undisturbed, which
    // matters because this runs while the doctor is still typing.
    serialize(doc) {
      const clone = doc.documentElement.cloneNode(true);
      const body = clone.querySelector("body");
      if (body) {
        body.removeAttribute("contenteditable");
        if (this.bodyStyle === null) body.removeAttribute("style");
        else body.setAttribute("style", this.bodyStyle);
      }
      return clone.outerHTML.split(CARET_ANCHOR).join("");
    },

    // ---------- table rows ----------
    rowsOf(container) {
      if (!container || !container.children) return [];
      return Array.from(container.children).filter(
        (el) => el.tagName === "TR"
      );
    },
    syncActiveRow() {
      const doc = this.doc();
      if (!doc || !this.editable) {
        this.activeRow = null;
        return;
      }
      const selection = doc.getSelection();
      let node = selection ? selection.anchorNode : null;
      if (node && node.nodeType === Node.TEXT_NODE) node = node.parentNode;
      const row = node && node.closest ? node.closest("tr") : null;
      // A header row is layout, not a medicine line — leave it alone.
      const inHead =
        row && row.parentNode && row.parentNode.tagName === "THEAD";
      this.activeRow = row && !inHead ? row : null;
      this.measureRow();
    },
    // Translates the row's position inside the iframe into this component's
    // box. offsetTop/offsetLeft are relative to .preview-wrap (it is the
    // positioned ancestor) and clientTop/clientLeft skip the iframe's border.
    measureRow() {
      const row = this.activeRow;
      const frame = this.$refs.frame;
      if (!row || !frame || !row.isConnected) {
        this.rowBox = null;
        return;
      }
      const rect = row.getBoundingClientRect();
      const viewTop = frame.offsetTop + frame.clientTop;
      const viewLeft = frame.offsetLeft + frame.clientLeft;
      // Scrolled out of the iframe's viewport: the controls would otherwise
      // hover over the letterhead, pointing at a row nobody can see.
      if (rect.bottom < 0 || rect.top > frame.clientHeight) {
        this.rowBox = null;
        return;
      }
      // A half-scrolled row would otherwise draw its outline across the hint
      // text and the page around the iframe, so clip it to what is visible.
      const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);
      const height = frame.clientHeight;
      const width = frame.clientWidth;
      const top = clamp(rect.top, 0, height);
      const bottom = clamp(rect.bottom, 0, height);
      const left = clamp(rect.left, 0, width);
      const right = clamp(rect.right, 0, width);
      // Half the control stack's height, so it stays inside the frame when the
      // row sits at the very top or bottom of the visible area.
      const margin = 34;
      this.rowBox = {
        top: viewTop + top,
        left: viewLeft + left,
        width: right - left,
        height: bottom - top,
        centerY:
          viewTop +
          clamp(rect.top + rect.height / 2, margin, height - margin),
        gutterX: viewLeft + width - 8,
      };
    },
    addRowBelow() {
      const row = this.activeRow;
      if (!row || !row.parentNode) return;
      const container = row.parentNode;
      const serial = this.isSerialColumn(container);
      const blank = this.templateRowFor(row);
      this.clearText(blank);
      container.insertBefore(blank, row.nextSibling);
      if (serial) this.applyNumbers(container);
      this.activeRow = blank;
      // With a "#" column the number is filled in already, so start the doctor
      // in the medicine cell rather than on top of it.
      this.focusCell(blank, serial && blank.cells.length > 1 ? 1 : 0);
      this.$nextTick(this.measureRow);
      this.emitNow();
    },
    deleteRow() {
      if (!this.canDeleteRow) return;
      const row = this.activeRow;
      // Removing a node directly is invisible to the browser's undo stack, so
      // Ctrl+Z will not bring the medicine back — ask first.
      if (!window.confirm("Delete this row? This cannot be undone with Ctrl+Z."))
        return;
      const container = row.parentNode;
      const serial = this.isSerialColumn(container);
      const fallback = row.nextElementSibling || row.previousElementSibling;
      container.removeChild(row);
      if (serial) this.applyNumbers(container);
      this.activeRow =
        fallback && fallback.tagName === "TR" ? fallback : null;
      if (this.activeRow) this.focusCell(this.activeRow, 0);
      this.$nextTick(this.measureRow);
      this.emitNow();
    },
    // Cloning the clicked row alone makes the new row's shape depend on which
    // row was clicked: Methylcobalamin carries no generic-name line, so a row
    // added from it would give the doctor nowhere to type one. Take each column
    // from the fullest version of it in the table instead, so every new row
    // offers the same slots as the richest row above.
    templateRowFor(row) {
      const blank = row.cloneNode(true);
      const rows = this.rowsOf(row.parentNode);
      Array.from(blank.cells || []).forEach((cell, index) => {
        if (this.secondLineOf(cell)) return;
        // Borrow the missing slots from the same column only. Swapping in
        // another row's whole cell would drag its styling along with it, and a
        // stray <strong> left in one row would then make every new row bold.
        const donor = rows
          .map((other) => other.cells && other.cells[index])
          .find((other) => other && this.secondLineOf(other));
        if (!donor) return;
        // Mirror both of the column's lines. Adding only the second one leaves
        // it as the cell's first element, and the doctor's opening keystroke
        // lands in the small grey line meant for the generic name.
        // The column's own emphasis if it has one, otherwise the table's — a
        // Frequency column whose rows are bare text still gets a bold first
        // line, matching the medicine cells beside it.
        const first =
          this.firstLineOf(donor) ||
          rows
            .reduce(
              (all, other) => all.concat(Array.from(other.cells || [])),
              []
            )
            .map((other) => this.firstLineOf(other))
            .find(Boolean);
        if (first && !this.wrapFirstLine(cell, first)) {
          const empty = first.cloneNode(false);
          empty.appendChild(cell.ownerDocument.createTextNode(CARET_ANCHOR));
          cell.insertBefore(empty, cell.firstChild || null);
        } else if (!first && !cell.textContent.trim()) {
          // The column writes its first line as bare text, so the new cell
          // needs bare text to type into — otherwise the appended second line
          // becomes the only thing the caret can reach.
          cell.insertBefore(
            cell.ownerDocument.createTextNode(CARET_ANCHOR),
            cell.firstChild || null
          );
        }
        this.addLineLike(cell, this.secondLineOf(donor));
      });
      return blank;
    },
    // Empties the copied row's text while keeping its cells and their inline
    // markup, so a new medicine line inherits the styling of the one above it.
    clearText(row) {
      const doc = row.ownerDocument;
      const walker = doc.createTreeWalker(row, NodeFilter.SHOW_TEXT);
      const texts = [];
      while (walker.nextNode()) texts.push(walker.currentNode);
      texts.forEach((text) => {
        // Anchor anywhere the doctor could type: inside every leaf element, so
        // the generic name lands in its own <small> rather than the <strong>
        // above it, and wherever real text sat directly in a cell, so a first
        // line written as bare text — "1-0-1" — stays reachable. Whitespace
        // between tags is only layout and is cleared away.
        const parent = text.parentElement;
        const hadContent = text.data.trim() !== "";
        const isLeaf = parent && !parent.firstElementChild;
        text.data = isLeaf || hadContent ? CARET_ANCHOR : "";
      });
      // A cell that held no text at all still needs somewhere to click into.
      Array.from(row.cells || []).forEach((cell) => {
        if (cell.textContent) return;
        const target = this.caretTargetIn(cell);
        target.insertBefore(
          doc.createTextNode(CARET_ANCHOR),
          target.firstChild || null
        );
      });
    },
    // Enter inside the drug name would break the line inside the <strong> and
    // the generic name would print bold. When the cell has an empty second line
    // waiting below the <br>, send the caret there instead — that is what the
    // doctor means by Enter here. Filled cells keep the browser's behaviour.
    onKeydown(event) {
      if (event.key !== "Enter" || event.shiftKey) return;
      const doc = this.doc();
      const selection = doc ? doc.getSelection() : null;
      if (!selection || !selection.isCollapsed) return;
      let node = selection.anchorNode;
      if (node && node.nodeType === Node.TEXT_NODE) node = node.parentNode;
      const cell = node && node.closest ? node.closest("td,th") : null;
      if (!cell) return;
      const waiting = this.emptySecondLineIn(cell, node);
      if (waiting) {
        event.preventDefault();
        this.placeCaretIn(waiting);
        return;
      }
      // Columns like Frequency are single-line in some prescriptions, so there
      // is nothing below to jump into — build the second line instead.
      const created = this.appendSecondLine(cell);
      if (!created) return;
      event.preventDefault();
      this.placeCaretIn(created);
      this.$nextTick(this.measureRow);
      this.emitNow();
    },
    appendSecondLine(cell) {
      // Mid-text Enter should split the line the way the browser does; this is
      // only for adding a line under what is already there.
      if (!this.caretAtEndOf(cell)) return null;
      const models = this.lineModelsFor(cell);
      if (!models.second) return null;
      // Enter turns a plain cell into the two-line shape its column uses, which
      // means the first line takes that shape's emphasis too.
      this.wrapFirstLine(cell, models.first);
      return this.addLineLike(cell, models.second);
    },
    // Both line shapes, taken from one donor cell so they stay a matched pair.
    // The cell's own column is asked first: borrowing the medicine column's
    // bold for an instructions cell would emphasise text the document never
    // emphasises there.
    lineModelsFor(cell) {
      const table = cell.closest("table");
      if (!table) return {};
      const cells = Array.from(table.querySelectorAll("td, th")).filter(
        (other) => other !== cell
      );
      const sameColumn = cells.filter(
        (other) => other.cellIndex === cell.cellIndex
      );
      const inColumn = sameColumn.find((other) => this.secondLineOf(other));
      const donor = inColumn || cells.find((other) => this.secondLineOf(other));
      if (!donor) return {};
      // Both shapes come from the donor, so the pair matches: an emphasised
      // first line above a small grey second one, the way the medicine cells
      // are written. The bold stops at the first line — wrapFirstLine takes
      // only the nodes above the break, so the line below stays grey.
      return {
        second: this.secondLineOf(donor),
        first: this.firstLineOf(donor) || this.anyFirstLineIn(table, cell),
      };
    },
    // ---------- repairing rows saved by the earlier caret bug ----------
    // Those rows have an empty first line above a second line holding the text
    // the doctor typed, so a medicine or frequency reads as a small grey note.
    misplacedCells() {
      const doc = this.doc();
      if (!doc) return [];
      return Array.from(doc.querySelectorAll("td, th")).filter((cell) =>
        this.misplacedIn(cell)
      );
    },
    // The line that should be sitting above, or null when the cell is fine.
    misplacedIn(cell) {
      const second = this.secondLineOf(cell);
      if (
        second &&
        this.textOf([second]) &&
        !this.textOf(this.firstLineNodes(cell))
      ) {
        return second;
      }
      // A cell holding nothing but a small grey line never got a first line at
      // all, so there is no second line to find by position — recognise it by
      // matching the shape other cells use for theirs.
      const elements = Array.from(cell.children);
      const model = this.anySecondLineIn(cell);
      if (!model || elements.length !== 1) return null;
      const only = elements[0];
      const outside = Array.from(cell.childNodes).filter(
        (node) => node !== only
      );
      if (this.textOf(outside) || !this.textOf([only])) return null;
      return only.tagName === model.tagName &&
        only.className === model.className
        ? only
        : null;
    },
    anySecondLineIn(cell) {
      const table = cell.closest("table");
      if (!table) return null;
      return (
        Array.from(table.querySelectorAll("td, th"))
          .filter((other) => other !== cell)
          .map((other) => this.secondLineOf(other))
          .find(Boolean) || null
      );
    },
    countMisplaced() {
      this.misplaced = this.editable ? this.misplacedCells().length : 0;
    },
    fixMisplaced() {
      const cells = this.misplacedCells();
      if (!cells.length) return;
      cells.forEach((cell) => this.liftSecondLine(cell));
      this.emitNow();
      this.countMisplaced();
      this.$nextTick(this.measureRow);
    },
    // Moves the text up into the first line and drops the now-empty second one.
    // Only the position changes — restyling a prescription automatically is not
    // this button's job.
    liftSecondLine(cell) {
      const second = this.misplacedIn(cell);
      if (!second) return;
      const moving = Array.from(second.childNodes);
      const target = this.firstLineOf(cell);
      if (target) {
        moving.forEach((node) => target.appendChild(node));
      } else {
        moving.forEach((node) => cell.insertBefore(node, second));
      }
      const before = second.previousSibling;
      if (
        before &&
        before.nodeType === Node.ELEMENT_NODE &&
        before.tagName === "BR"
      ) {
        before.parentNode.removeChild(before);
      }
      second.parentNode.removeChild(second);
    },
    // Falls back to the emphasis used by any two-line cell in the table, for a
    // column that writes its own first line as bare text: a Frequency cell
    // still wants "1-0-1" to read like a drug name above its small grey line.
    anyFirstLineIn(table, cell) {
      if (!table) return null;
      return (
        Array.from(table.querySelectorAll("td, th"))
          .filter((other) => other !== cell)
          .map((other) => this.firstLineOf(other))
          .find(Boolean) || null
      );
    },
    // The element holding a cell's first line, when it has a second one below.
    firstLineOf(cell) {
      const second = this.secondLineOf(cell);
      if (!second) return null;
      const nodes = Array.from(cell.childNodes);
      for (let i = nodes.indexOf(second) - 1; i >= 0; i--) {
        const node = nodes[i];
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== "BR") {
          return node;
        }
      }
      return null;
    },
    // The nodes making up a cell's first line: everything above the break or
    // the second line, whichever comes first.
    firstLineNodes(cell) {
      const nodes = Array.from(cell.childNodes);
      const br = nodes.findIndex(
        (node) => node.nodeType === Node.ELEMENT_NODE && node.tagName === "BR"
      );
      const second = this.secondLineOf(cell);
      let end = br >= 0 ? br : nodes.length;
      if (second) end = Math.min(end, nodes.indexOf(second));
      return nodes.slice(0, end);
    },
    textOf(nodes) {
      return nodes
        .map((node) => node.textContent)
        .join("")
        .split(CARET_ANCHOR)
        .join("")
        .trim();
    },
    wrapFirstLine(cell, model) {
      if (!model) return null;
      const nodes = Array.from(cell.childNodes);
      // Wrap the first line only. Sweeping up the whole cell would pull an
      // existing second line inside the <strong> as well, and every line the
      // doctor added would end up bold.
      const firstLine = this.firstLineNodes(cell);
      const end = firstLine.length;
      // Only bare text needs wrapping; content already in an element keeps the
      // styling it has.
      const bare = firstLine.some(
        (node) =>
          node.nodeType === Node.TEXT_NODE &&
          node.textContent.split(CARET_ANCHOR).join("").trim()
      );
      if (!bare) return null;
      const wrapper = model.cloneNode(false);
      firstLine.forEach((node) => wrapper.appendChild(node));
      cell.insertBefore(wrapper, nodes[end] || null);
      return wrapper;
    },
    // Adds an empty line to a cell shaped like an existing one elsewhere in the
    // table, so it prints in that same small grey style.
    addLineLike(cell, model) {
      const doc = cell.ownerDocument;
      const line = model.cloneNode(false);
      line.appendChild(doc.createTextNode(CARET_ANCHOR));
      // A block-level line already starts on its own row; only an inline one
      // such as <small> or <span> needs a break put in front of it. The model
      // is the one in the live document, so its computed style is real.
      const win = this.$refs.frame.contentWindow;
      const display = win ? win.getComputedStyle(model).display : "inline";
      if (display.indexOf("inline") === 0) {
        cell.appendChild(doc.createElement("br"));
      }
      cell.appendChild(line);
      return line;
    },
    caretAtEndOf(cell) {
      const doc = this.doc();
      const selection = doc.getSelection();
      const range = doc.createRange();
      range.selectNodeContents(cell);
      range.setStart(selection.anchorNode, selection.anchorOffset);
      // Only anchors and whitespace may sit between the caret and the end.
      return range.toString().split(CARET_ANCHOR).join("").trim() === "";
    },
    // The element holding a cell's second line. Prescriptions write this two
    // ways — a <br> with an element after it, or two stacked elements — so
    // matching only the <br> form leaves the other one with no second line.
    secondLineOf(cell) {
      const br = Array.from(cell.childNodes).find(
        (child) => child.nodeType === Node.ELEMENT_NODE && child.tagName === "BR"
      );
      if (br) {
        // Browsers leave a filler <br> behind after a line break, so step past
        // any run of them. A <br> is never a line of its own — treating one as
        // the second line would have the caret placed inside an element that
        // cannot hold text.
        let next = br.nextElementSibling;
        while (next && next.tagName === "BR") next = next.nextElementSibling;
        if (next) return next;
      }
      const elements = Array.from(cell.children).filter(
        (child) => child.tagName !== "BR"
      );
      if (!elements.length) return null;
      const last = elements[elements.length - 1];
      if (elements.length >= 2) return last;
      // A lone element under plain text is a second line too — the shape a
      // cell takes once Enter has added one to "1-0-1".
      const nodes = Array.from(cell.childNodes);
      const before = nodes
        .slice(0, nodes.indexOf(last))
        .map((node) => node.textContent)
        .join("")
        .split(CARET_ANCHOR)
        .join("")
        .trim();
      return before ? last : null;
    },
    emptySecondLineIn(cell, current) {
      const line = this.secondLineOf(cell);
      if (!line || line.contains(current)) return null;
      // Only when it is still blank; otherwise Enter would jump over content.
      const text = line.textContent.split(CARET_ANCHOR).join("").trim();
      return text ? null : line;
    },
    placeCaretIn(element) {
      const doc = this.doc();
      let first = element.firstChild;
      if (!first || first.nodeType !== Node.TEXT_NODE) {
        first = doc.createTextNode(CARET_ANCHOR);
        element.insertBefore(first, element.firstChild || null);
      }
      const range = doc.createRange();
      range.setStart(first, first.data.length);
      range.collapse(true);
      const selection = doc.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    },
    // The innermost element a cell's text lives in — <strong> in a medicine
    // cell, the cell itself in a plain one. Stops at <br>, which separates the
    // drug name from the generic name rather than wrapping anything.
    caretTargetIn(cell) {
      let node = cell;
      for (;;) {
        // Whichever comes first: real text, or an element to descend into.
        // Descending past text that precedes an element is what dropped the
        // caret into the small grey line when a cell's first line was bare
        // text with a <small> after it.
        const next = Array.from(node.childNodes).find(
          (child) =>
            (child.nodeType === Node.TEXT_NODE && child.data.length) ||
            (child.nodeType === Node.ELEMENT_NODE && child.tagName !== "BR")
        );
        if (!next || next.nodeType === Node.TEXT_NODE) return node;
        node = next;
      }
    },
    // True only when the first column already reads exactly 1..n, which marks
    // it as a serial number rather than data. Must be asked before the row is
    // added or removed, since either mutation breaks the sequence it looks for.
    isSerialColumn(container) {
      const rows = this.rowsOf(container);
      if (!rows.length) return false;
      return rows.every((row, i) => {
        const cell = row.cells && row.cells[0];
        return cell && cell.textContent.trim() === String(i + 1);
      });
    },
    applyNumbers(container) {
      this.rowsOf(container).forEach((row, i) => {
        if (row.cells && row.cells[0]) {
          row.cells[0].textContent = String(i + 1);
        }
      });
    },
    focusCell(row, index) {
      const doc = this.doc();
      const frame = this.$refs.frame;
      const cell = row.cells && row.cells[index];
      if (!doc || !frame || !frame.contentWindow || !cell) return;
      // Focus has to come first. Clicking the button moved it out of the iframe,
      // and a browser restoring focus also restores the selection it had then —
      // which would silently undo a range set beforehand and leave the caret in
      // the row the doctor clicked, not the row just added.
      frame.contentWindow.focus();
      const target = this.caretTargetIn(cell);
      const first = target.firstChild;
      const range = doc.createRange();
      if (first && first.nodeType === Node.TEXT_NODE) {
        // Sit after the anchor in a new cell, before existing text in an old one
        range.setStart(first, first.data === CARET_ANCHOR ? 1 : 0);
        range.collapse(true);
      } else {
        range.selectNodeContents(target);
        range.collapse(true);
      }
      const selection = doc.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    },
  },
};
</script>

<style scoped>
/* The positioned ancestor the overlay measurements are relative to. */
.preview-wrap {
  position: relative;
}
/* Reserved so the iframe does not shift when the hint text changes. */
.preview-hint {
  min-height: 1.2em;
  margin-bottom: 0.25rem;
}
.html-preview {
  display: block;
  width: 100%;
  height: 600px;
}
.row-outline {
  position: absolute;
  pointer-events: none;
  border: 2px solid rgba(0, 123, 255, 0.5);
  border-radius: 2px;
  z-index: 1;
}
.row-tools {
  position: absolute;
  display: flex;
  flex-direction: column;
  /* Anchors its right edge at gutterX and centres it on the row. */
  transform: translate(-100%, -50%);
  z-index: 2;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
}
</style>
